import type { Property } from '~/core/entities/property';

import { propertyImages } from '~/composables/shorts/mappers';
import { PropertyPublicationStatus } from '~/core/entities/api/ApiProperty';
import { hasShortsVideo } from '~/utils';

interface UseShortsEntryAvailabilityOptions {
	shouldAddRobotsMeta?: Ref<boolean>;
}

export function useShortsEntryAvailability(
	entryProperty: Ref<Property | null>,
	options: UseShortsEntryAvailabilityOptions = {}
) {
	const isEntryPropertyPublished = computed(
		() =>
			entryProperty.value?.publicationStatus ===
			PropertyPublicationStatus.PUBLISHED
	);

	const isEntryPropertyAvailable = computed(() => {
		if (!isEntryPropertyPublished.value || !entryProperty.value) return false;
		if (hasShortsVideo(entryProperty.value)) return true;
		return propertyImages(entryProperty.value).length > 0;
	});
	const isUnavailableEntryState = computed(
		() => !!entryProperty.value && !isEntryPropertyAvailable.value
	);
	const shouldAddRobotsMeta = computed(
		() =>
			isUnavailableEntryState.value &&
			(options.shouldAddRobotsMeta?.value ?? true)
	);

	const event = useRequestEvent();
	if (event && isUnavailableEntryState.value) {
		setResponseStatus(event, 410, 'Gone');
	}

	useHead({
		meta: computed(() =>
			shouldAddRobotsMeta.value
				? [
						{
							name: 'robots',
							content: 'noindex, nofollow'
						}
					]
				: []
		)
	});

	return {
		isEntryPropertyPublished,
		isEntryPropertyAvailable,
		isUnavailableEntryState
	};
}
